import createSagaMiddleware from "redux-saga";
import {
  activityMiddlewares,
  activityReducers,
} from "@b2bportal/core-activities";
import {
  carMiddlewares,
  carReducers,
  carsPostBookingApi as coreCarsPostBookingApi,
} from "@b2bportal/core-cars";
import { disruptionRootReducer } from "@b2bportal/core-disruption";
import {
  flightRtkQueryReducers,
  flightsPostBookingApi,
} from "@b2bportal/core-flights";
import { hotelMiddlewares, hotelReducers } from "@b2bportal/core-hotels";
import { selfServeRootReducer } from "@b2bportal/core-self-serve/store";
import {
  travelerReducers,
  travelerMiddlewares,
} from "@b2bportal/core-traveler";
import { tripsRootReducer } from "@b2bportal/core-trips";
import {
  walletOfferMiddlewares,
  walletOfferReducers,
} from "@b2bportal/core-wallet-offer";
import { authReducer } from "@hopper-b2b/self-serve";
import {
  combineSlices,
  configureStore,
  createDynamicMiddleware,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  experimentsMiddleware,
  experimentsReducers,
} from "@tenant/experiments";
import { htsAssistPrewarmApi } from "../api/htsAssist/prewarmApi";
import accountReducer from "../modules/account/slice";
import { carsPostBookingApi } from "../modules/cars-postbooking";
import { gridV2Reducer } from "../modules/grid-v2/slice";
import { homesPostBookingApi } from "../modules/homes-postbooking";
import {
  hotelFundedOffersApiMiddlewares,
  hotelFundedOffersReducers,
} from "../modules/hotel-funded-offers/store";
import {
  packagesPostBookingApiMiddlewares,
  packagesPostBookingReducers,
} from "../modules/packages-postBooking";
import { flightsReducer } from "./flights/slice";
import { htsAssistEntryReducer } from "./htsAssistEntryState";
import { loadStateAfterLanguageChange } from "./languageChangePersistence";
import { packagesReducer } from "./packages/slice";
import {
  tenantFlightsMiddleware,
  tenantFlightsReducers,
} from "./tenantFlights";

export const dynamicMiddleware = createDynamicMiddleware();

const sagaMiddleware = createSagaMiddleware();

// Filter out core carsPostBookingApi middleware since RBC overrides it
const rbcCarMiddlewares = carMiddlewares.filter(
  (mw) => mw !== coreCarsPostBookingApi.middleware
);

export const middleware = [
  sagaMiddleware,
  ...hotelMiddlewares,
  ...walletOfferMiddlewares,
  ...rbcCarMiddlewares,
  carsPostBookingApi.middleware,
  flightsPostBookingApi.middleware,
  homesPostBookingApi.middleware,
  ...travelerMiddlewares,
  ...tenantFlightsMiddleware,
  ...experimentsMiddleware,
  ...activityMiddlewares,
  ...packagesPostBookingApiMiddlewares,
  ...hotelFundedOffersApiMiddlewares,
  htsAssistPrewarmApi.middleware,
];

// Filter out core carsPostBookingApi reducer since RBC overrides it
const {
  [coreCarsPostBookingApi.reducerPath]: _coreCarPostBooking,
  ...rbcCarReducers
} = carReducers;

export const reducer = combineSlices({
  auth: authReducer,
  flights: flightsReducer,
  trips: tripsRootReducer,
  disruption: disruptionRootReducer,
  ...hotelReducers,
  ...walletOfferReducers,
  packages: packagesReducer,
  ...rbcCarReducers,
  [carsPostBookingApi.reducerPath]: carsPostBookingApi.reducer,
  ...flightRtkQueryReducers,
  [homesPostBookingApi.reducerPath]: homesPostBookingApi.reducer,
  ...travelerReducers,
  ...tenantFlightsReducers,
  account: accountReducer,
  gridV2: gridV2Reducer,
  ...experimentsReducers,
  ...activityReducers,
  ...packagesPostBookingReducers,
  ...hotelFundedOffersReducers,
  selfServe: selfServeRootReducer,
  htsAssistEntry: htsAssistEntryReducer,
  [htsAssistPrewarmApi.reducerPath]: htsAssistPrewarmApi.reducer,
});

export type RootState = ReturnType<typeof reducer>;

// Load persisted state from sessionStorage if this is a language change reload
let preloadedState: Partial<RootState> | undefined;
try {
  preloadedState = loadStateAfterLanguageChange();
} catch (error) {
  console.error("[Store] Error loading preloaded state:", error);
  preloadedState = undefined;
}

const store = configureStore({
  reducer,
  preloadedState,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(middleware)
      .concat(dynamicMiddleware.middleware),
});

setupListeners(store.dispatch);

// const rootSaga = {}
// // Run the root saga after creating the store
// sagaMiddleware.run(rootSaga);

export type AppDispatch = typeof store.dispatch;
export default store;

export * from "./packages";
